import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

/**
 * Componente de celda de texto simple
 */
export const TextCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="medium">
    {value || ""}
  </MDTypography>
);

TextCell.propTypes = {
  value: PropTypes.any,
};

/**
 * Componente de checkbox para selección múltiple
 */
export const CheckboxCell = ({ checked, onChange }) => (
  <MDBox display="flex" justifyContent="center" alignItems="center">
    <Checkbox
      checked={checked}
      onChange={onChange}
      sx={{
        color: "#7b809a",
        "&.Mui-checked": {
          color: "#49a3f1",
        },
      }}
    />
  </MDBox>
);

CheckboxCell.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

/*
 * Indicador circular de porcentaje con color según rango de ajuste al cargo
 * Verde (>= 80%): Excelente ajuste
 * Azul (70-79%): Buen ajuste
 * Amarillo (60-69%): Ajuste medio
 * Rojo (< 60%): Bajo ajuste
 */
export const CircularProgressIndicator = ({ percentage }) => {
  const getColor = (value) => {
    if (value >= 80) return "#4caf50"; // Verde - Excelente ajuste
    if (value >= 70) return "#2196f3"; // Azul - Buen ajuste
    if (value >= 60) return "#ff9800"; // Amarillo - Ajuste medio
    return "#f44336"; // Rojo - Bajo ajuste
  };

  const color = getColor(percentage);

  return (
    <MDBox display="flex" alignItems="center" justifyContent="center">
      <Box position="relative" display="inline-flex">
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={45}
          thickness={5}
          sx={{
            color: color,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <MDTypography variant="caption" fontWeight="bold" color="text">
            {`${percentage}%`}
          </MDTypography>
        </Box>
      </Box>
    </MDBox>
  );
};

CircularProgressIndicator.propTypes = {
  percentage: PropTypes.number.isRequired,
};

/**
 * Badge de estado de integridad laboral
 * hired -> Verde (Contratado)
 * interview -> Azul (En Entrevista)
 * reject -> Rojo (Rechazado)
 * pending -> Amarillo (Pendiente)
 */
export const StatusBadge = ({ status }) => {
  const statusConfig = {
    hired: {
      color: "success",
      label: "Contratado",
      customColor: null,
    },
    interview: {
      color: "info",
      label: "Entrevista",
      customColor: "#2196f3", // Azul personalizado
    },
    reject: {
      color: "error",
      label: "Rechazado",
      customColor: null,
    },
    pending: {
      color: "warning",
      label: "Pendiente",
      customColor: null,
    },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <MDBox display="flex" justifyContent="center" alignItems="center">
      <MDBadge
        badgeContent={config.label}
        color={config.color}
        variant="gradient"
        size="sm"
        sx={{
          "& .MuiBadge-badge": {
            padding: "8px 12px",
            fontSize: "0.75rem",
            fontWeight: "bold",
            ...(config.customColor && {
              background: `linear-gradient(195deg, ${config.customColor}, ${config.customColor})`,
            }),
          },
        }}
      />
    </MDBox>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.oneOf(["hired", "interview", "reject", "pending"]).isRequired,
};

/**
 * Botón para generar informe individual
 */
export const GenerateReportButton = ({ candidateId, onGenerateReport, isLoading }) => {
  const handleClick = async () => {
    try {
      await onGenerateReport(candidateId);
    } catch (error) {
      console.error("Error en GenerateReportButton:", error);
    }
  };

  return (
    <MDBox display="flex" justifyContent="center" alignItems="center">
      <MDButton
        variant="gradient"
        color="info"
        size="small"
        onClick={handleClick}
        disabled={isLoading}
      >
        <Icon>{isLoading ? "hourglass_empty" : "assignment"}</Icon>&nbsp;
        {isLoading ? "Generando..." : "Informe"}
      </MDButton>
    </MDBox>
  );
};

GenerateReportButton.propTypes = {
  candidateId: PropTypes.string.isRequired,
  onGenerateReport: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

/**
 * Celda de fecha formateada
 */
export const DateCell = ({ date }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      const [year, month, day] = dateString.split("-");
      return `${year}-${month}-${day}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <MDTypography variant="caption" fontWeight="medium">
      {formatDate(date)}
    </MDTypography>
  );
};

DateCell.propTypes = {
  date: PropTypes.string,
};

/**
 * Semáforo de Integridad Laboral
 * 
 * Muestra el Índice de Integridad Laboral calculado por el backend.
 * El backend calcula: indice_riesgo = test_valores + test_canciones + test_dibujos
 * 
 * REGLAS DEL SEMÁFORO según especificaciones:
 * 
 * | Índice | Interpretación                              | Semáforo  | Nivel       |
 * |--------|---------------------------------------------|-----------|-------------|
 * |   0    | Ningún test indica problemas con la norma   | 🟢 Verde  | Riesgo Bajo |
 * |   1    | Un test indica problemas con la norma       | 🟡 Amarillo| Riesgo Medio|
 * |   2    | Dos tests indican problemas con la norma    | 🔴 Rojo   | Riesgo Alto |
 * |   3    | Tres tests indican problemas con la norma   | 🔴 Rojo   | Riesgo Alto |
 * 
 * @param {number} indiceRiesgo - Valor del 0 al 3 que viene del backend
 */
export const IntegrityTrafficLight = ({ indiceRiesgo }) => {
  // Validar y normalizar el índice de riesgo
  const riesgo = indiceRiesgo !== undefined && indiceRiesgo !== null ? indiceRiesgo : 0;
  
  // Determinar semáforo y nivel de riesgo según especificaciones
  let color, bgColor, label, nivelRiesgo, icon;
  
  if (riesgo === 0) {
    // Semáforo Verde: Ningún test indica problemas (indice_riesgo == 0)
    color = "success";
    bgColor = "#4caf50"; // Verde
    label = "Bajo";
    nivelRiesgo = "Riesgo Bajo";
    icon = "🟢";
  } else if (riesgo === 1) {
    // Semáforo Amarillo: Un test indica problemas (indice_riesgo == 1)
    color = "warning";
    bgColor = "#ff9800"; // Amarillo/Naranja
    label = "Medio";
    nivelRiesgo = "Riesgo Medio";
    icon = "🟡";
  } else {
    // Semáforo Rojo: Dos o tres tests indican problemas (indice_riesgo >= 2)
    color = "error";
    bgColor = "#f44336"; // Rojo
    label = "Alto";
    nivelRiesgo = "Riesgo Alto";
    icon = "🔴";
  }
  
  return (
    <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      {/* Badge principal con el índice y nivel de riesgo */}
      <MDBadge
        badgeContent={`${riesgo}/3 - ${label}`}
        color={color}
        variant="gradient"
        size="sm"
        sx={{
          "& .MuiBadge-badge": {
            padding: "8px 12px",
            fontSize: "0.75rem",
            fontWeight: "bold",
            background: `linear-gradient(195deg, ${bgColor}, ${bgColor})`,
          },
        }}
      />
      
      {/* Mostrar el nivel de riesgo debajo */}
      <MDTypography 
        variant="caption" 
        fontSize="0.65rem" 
        color="text" 
        sx={{ mt: 0.5, textAlign: "center" }}
      >
        {nivelRiesgo}
      </MDTypography>
    </MDBox>
  );
};

IntegrityTrafficLight.propTypes = {
  indiceRiesgo: PropTypes.number, // Valor del 0 al 3 (viene del backend)
};

IntegrityTrafficLight.defaultProps = {
  indiceRiesgo: 0, // Por defecto muestra verde si no hay datos
};